import { MGPValidation, MGPOptional, Utils, MGPMap } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { CoordFailure } from '../../jscaip/Coord';
import { DodecaHexaDirection } from '../../jscaip/DodecaHexaDirection';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { GameStatus } from '../../jscaip/GameStatus';
import { AbstractNInARowHelper } from '../../jscaip/NInARowHelper';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { FourStatePieceGameStateWithTable } from '../../jscaip/state/FourStatePieceGameStateWithTable';
import { MGPValidators } from '../../utils/MGPValidator';
export class HexodiaNode extends GameNode {
}
export class HexodiaRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static helpers = new MGPMap();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Hexodia`,
        config: {
            size: new NumberConfig(12, RulesConfigDescriptionLocalizable.SIZE, MGPValidators.range(1, 99)),
            nInARow: new NumberConfig(6, RulesConfigDescriptionLocalizable.ALIGNMENT_SIZE, MGPValidators.range(1, 99)),
            numberOfDrops: new NumberConfig(2, RulesConfigDescriptionLocalizable.NUMBER_OF_DROPS, MGPValidators.range(1, 99)),
        },
    });
    static get() {
        if (HexodiaRules.singleton.isAbsent()) {
            HexodiaRules.singleton = MGPOptional.of(new HexodiaRules());
        }
        return HexodiaRules.singleton.get();
    }
    static getHexodiaHelper(config) {
        return HexodiaRules.getHexodiaHelperBySize(config.nInARow);
    }
    static getHexodiaHelperBySize(size) {
        if (HexodiaRules.helpers.get(size).isAbsent()) {
            const helper = new AbstractNInARowHelper((piece) => piece.getPlayer(), size, DodecaHexaDirection.factory.all);
            HexodiaRules.helpers.put(size, helper);
        }
        return HexodiaRules.helpers.get(size).get();
    }
    static getVictoriousCoords(state, config) {
        return HexodiaRules.getHexodiaHelper(config).getVictoriousCoord(state);
    }
    getRulesConfigDescription() {
        return HexodiaRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const size = config.size;
        const boardSize = (size * 2) - 1;
        const maximumDiagonalIndex = (3 * size) - 2;
        const board = TableUtils.create(boardSize, boardSize, FourStatePiece.UNREACHABLE);
        for (let y = 0; y < boardSize; y++) {
            for (let x = 0; x < boardSize; x++) {
                const diagonalIndex = x + y;
                if (size - 2 < diagonalIndex && diagonalIndex < maximumDiagonalIndex) {
                    board[y][x] = FourStatePiece.EMPTY;
                }
            }
        }
        return new FourStatePieceGameStateWithTable(board, 0);
    }
    applyLegalMove(move, state) {
        const player = FourStatePiece.ofPlayer(state.getCurrentPlayer());
        const newBoard = state.getCopiedBoard();
        for (const coord of move.coords) {
            newBoard[coord.y][coord.x] = player;
        }
        return new FourStatePieceGameStateWithTable(newBoard, state.turn + 1);
    }
    isLegal(move, state, config) {
        const numberOfDrops = move.coords.size();
        if (state.turn === 0) {
            Utils.assert(numberOfDrops === 1, 'HexodiaMove should only drop one piece at first turn');
        }
        else {
            const remainingSpaces = TableUtils.count(state.board, FourStatePiece.EMPTY);
            const requiredDrop = Math.min(remainingSpaces, config.numberOfDrops);
            Utils.assert(numberOfDrops === requiredDrop, 'HexodiaMove should have exactly ' + config.numberOfDrops + ' drops (got ' + numberOfDrops + ')');
        }
        return this.isLegalDrop(move, state);
    }
    isLegalDrop(move, state) {
        for (const coord of move.coords) {
            if (state.isNotOnBoard(coord)) {
                return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(coord));
            }
            if (state.getPieceAt(coord).isPlayer()) {
                return MGPValidation.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SQUARE());
            }
        }
        return MGPValidation.SUCCESS;
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const victoriousCoord = HexodiaRules.getVictoriousCoords(state, config);
        if (victoriousCoord.length > 0) {
            return GameStatus.getVictory(state.getCurrentOpponent());
        }
        if (TableUtils.contains(state.board, FourStatePiece.EMPTY)) {
            return GameStatus.ONGOING;
        }
        else {
            return GameStatus.DRAW;
        }
    }
}
